
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Filter } from 'lucide-react';
import { MINNESOTA_COUNTIES, WAIVER_TYPES, CARE_LEVELS, ACCESSIBILITY_OPTIONS } from '@/types/listings';

interface ListingFiltersProps {
  selectedCounties: string[];
  selectedWaivers: string[];
  selectedAccessibility: string[];
  selectedCareLevel: string;
  priceRange: [number, number];
  bedsAvailable: number;
  ageGroup: string;
  genderAccepted: string;
  onCountyChange: (county: string, checked: boolean) => void;
  onWaiverChange: (waiver: string, checked: boolean) => void;
  onAccessibilityChange: (accessibility: string, checked: boolean) => void;
  onCareLevelChange: (level: string) => void;
  onPriceRangeChange: (range: [number, number]) => void;
  onBedsAvailableChange: (beds: number) => void;
  onAgeGroupChange: (age: string) => void;
  onGenderAcceptedChange: (gender: string) => void;
  onClearFilters: () => void;
}

const ListingFilters: React.FC<ListingFiltersProps> = ({
  selectedCounties,
  selectedWaivers,
  selectedAccessibility,
  selectedCareLevel,
  priceRange,
  bedsAvailable,
  ageGroup,
  genderAccepted,
  onCountyChange,
  onWaiverChange,
  onAccessibilityChange,
  onCareLevelChange,
  onPriceRangeChange,
  onBedsAvailableChange,
  onAgeGroupChange,
  onGenderAcceptedChange,
  onClearFilters,
}) => {
  const hasActiveFilters = selectedCounties.length > 0 || selectedWaivers.length > 0 || 
    selectedAccessibility.length > 0 || (selectedCareLevel && selectedCareLevel !== 'all') || 
    priceRange[0] > 0 || priceRange[1] < 1000 || bedsAvailable > 1 || 
    (ageGroup && ageGroup !== 'all') || (genderAccepted && genderAccepted !== 'all');

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Advanced Filters
          </CardTitle>
          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={onClearFilters}>
              <X className="h-4 w-4 mr-2" />
              Clear All
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Active Filters Summary */}
        {hasActiveFilters && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Active Filters:</Label>
            <div className="flex flex-wrap gap-2">
              {selectedCounties.map(county => (
                <Badge key={county} variant="secondary" className="text-xs">
                  {county}
                  <X 
                    className="h-3 w-3 ml-1 cursor-pointer" 
                    onClick={() => onCountyChange(county, false)}
                  />
                </Badge>
              ))}
              {selectedWaivers.map(waiver => (
                <Badge key={waiver} variant="secondary" className="text-xs">
                  {waiver}
                  <X 
                    className="h-3 w-3 ml-1 cursor-pointer" 
                    onClick={() => onWaiverChange(waiver, false)}
                  />
                </Badge>
              ))}
              {selectedAccessibility.map(feature => (
                <Badge key={feature} variant="secondary" className="text-xs">
                  {feature}
                  <X 
                    className="h-3 w-3 ml-1 cursor-pointer" 
                    onClick={() => onAccessibilityChange(feature, false)}
                  />
                </Badge>
              ))}
              {selectedCareLevel && selectedCareLevel !== 'all' && (
                <Badge variant="secondary" className="text-xs">
                  {selectedCareLevel}
                  <X 
                    className="h-3 w-3 ml-1 cursor-pointer" 
                    onClick={() => onCareLevelChange('all')}
                  />
                </Badge>
              )}
              {ageGroup && ageGroup !== 'all' && (
                <Badge variant="secondary" className="text-xs">
                  {ageGroup}
                  <X 
                    className="h-3 w-3 ml-1 cursor-pointer" 
                    onClick={() => onAgeGroupChange('all')}
                  />
                </Badge>
              )}
              {genderAccepted && genderAccepted !== 'all' && (
                <Badge variant="secondary" className="text-xs">
                  {genderAccepted}
                  <X 
                    className="h-3 w-3 ml-1 cursor-pointer" 
                    onClick={() => onGenderAcceptedChange('all')}
                  />
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Counties Filter */}
        <div className="space-y-2">
          <Label className="font-medium">Counties</Label>
          <div className="max-h-32 overflow-y-auto space-y-1 bg-muted/50 p-2 rounded-md">
            {MINNESOTA_COUNTIES.map(county => (
              <div key={county} className="flex items-center space-x-2">
                <Checkbox
                  id={county}
                  checked={selectedCounties.includes(county)}
                  onCheckedChange={(checked) => onCountyChange(county, checked as boolean)}
                />
                <Label htmlFor={county} className="text-sm cursor-pointer">
                  {county}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Waiver Types Filter */}
        <div className="space-y-2">
          <Label className="font-medium">Waiver Types</Label>
          <div className="max-h-32 overflow-y-auto space-y-1 bg-muted/50 p-2 rounded-md">
            {WAIVER_TYPES.map(waiver => (
              <div key={waiver} className="flex items-center space-x-2">
                <Checkbox
                  id={waiver}
                  checked={selectedWaivers.includes(waiver)}
                  onCheckedChange={(checked) => onWaiverChange(waiver, checked as boolean)}
                />
                <Label htmlFor={waiver} className="text-sm cursor-pointer">
                  {waiver}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Accessibility Features Filter */}
        <div className="space-y-2">
          <Label className="font-medium">Accessibility Features</Label>
          <div className="space-y-1 bg-muted/50 p-2 rounded-md">
            {ACCESSIBILITY_OPTIONS.map(feature => (
              <div key={feature} className="flex items-center space-x-2">
                <Checkbox
                  id={feature}
                  checked={selectedAccessibility.includes(feature)}
                  onCheckedChange={(checked) => onAccessibilityChange(feature, checked as boolean)}
                />
                <Label htmlFor={feature} className="text-sm cursor-pointer">
                  {feature}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Care Level Filter */}
        <div className="space-y-2">
          <Label className="font-medium">Care Level</Label>
          <Select value={selectedCareLevel} onValueChange={onCareLevelChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select care level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Care Levels</SelectItem>
              {CARE_LEVELS.map(level => (
                <SelectItem key={level} value={level}>{level}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Rental Price Range Filter */}
        <div className="space-y-2">
          <Label className="font-medium">
            Monthly Rental Price Range: ${priceRange[0]} - ${priceRange[1]}/month
          </Label>
          <Slider
            value={priceRange}
            onValueChange={(value) => onPriceRangeChange(value as [number, number])}
            min={0}
            max={1000}
            step={50}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>$0/month</span>
            <span>$1,000+/month</span>
          </div>
        </div>

        {/* Beds Available Filter */}
        <div className="space-y-2">
          <Label className="font-medium">Minimum Beds Available: {bedsAvailable}</Label>
          <Slider
            value={[bedsAvailable]}
            onValueChange={(value) => onBedsAvailableChange(value[0])}
            min={1}
            max={10}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>1</span>
            <span>10+</span>
          </div>
        </div>

        {/* Age Group Filter */}
        <div className="space-y-2">
          <Label className="font-medium">Age Group</Label>
          <Select value={ageGroup} onValueChange={onAgeGroupChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select age group" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ages</SelectItem>
              <SelectItem value="18+">18+</SelectItem>
              <SelectItem value="55+">55+</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Gender Accepted Filter */}
        <div className="space-y-2">
          <Label className="font-medium">Gender Accepted</Label>
          <Select value={genderAccepted} onValueChange={onGenderAcceptedChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select gender" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Genders</SelectItem>
              <SelectItem value="Male">Male</SelectItem>
              <SelectItem value="Female">Female</SelectItem>
              <SelectItem value="Both">Both</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
};

export default ListingFilters;
